import { useState } from 'react';
import { CardTitle } from '@shared/ui/Card';
import { FileDropZone } from '@shared/ui/FileDropZone';
import {
  useUploadMeetingRecordingMutation,
  useCreateMeetingSeriesMutation,
} from '@shared/api';
import {
  UploadCard,
  UploadDesc,
  FormRow,
  TitleInput,
  ContextSelect,
  MiniBtn,
  Note,
} from './styles';
import { RECORDING_ACCEPT, type UploadContextProps } from './types';

/** Панель загрузки записи (под правом записи). Создаёт совещание + запись и ставит
 *  ASR-задачу; список совещаний обновляется по инвалидации тега. */
export function UploadPanel({ seriesList, projects }: UploadContextProps) {
  const [upload, { isLoading }] = useUploadMeetingRecordingMutation();
  const [createSeries, { isLoading: creatingSeries }] = useCreateMeetingSeriesMutation();
  const [title, setTitle] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [showNewSeries, setShowNewSeries] = useState(false);
  const [newSeriesTitle, setNewSeriesTitle] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCreateSeries() {
    if (!newSeriesTitle.trim()) return;
    try {
      const res = await createSeries({
        title: newSeriesTitle.trim(),
        project_id: projectId || null,
      }).unwrap();
      setSeriesId(res.series.id);
      setNewSeriesTitle('');
      setShowNewSeries(false);
    } catch {
      setError('Не удалось создать серию совещаний.');
    }
  }

  async function handleFile(file: File) {
    setError(null);
    setDone(null);
    const form = new FormData();
    form.append('file', file);
    if (title.trim()) form.append('title', title.trim());
    if (seriesId) form.append('series_id', seriesId);
    if (projectId) form.append('project_id', projectId);
    try {
      const res = await upload(form).unwrap();
      setDone(res.meeting.title);
      setTitle('');
    } catch (err) {
      setError('Не удалось загрузить запись. Проверьте файл и попробуйте снова.');
      // eslint-disable-next-line no-console
      console.error('uploadMeetingRecording failed', err);
    }
  }

  return (
    <UploadCard>
      <div>
        <CardTitle as="span">Загрузить запись совещания</CardTitle>
        <UploadDesc>
          Аудио или видеозапись совещания. Система автоматически распознает речь по спикерам,
          сформирует черновик протокола с решениями и поручениями и уведомит вас по готовности.
        </UploadDesc>
      </div>
      <FormRow>
        <TitleInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Название совещания (необязательно)"
          aria-label="Название совещания"
        />
        <ContextSelect
          value={seriesId}
          onChange={(e) => setSeriesId(e.target.value)}
          aria-label="Серия совещаний"
        >
          <option value="">— без серии —</option>
          {seriesList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </ContextSelect>
        <MiniBtn type="button" onClick={() => setShowNewSeries((v) => !v)}>
          {showNewSeries ? 'Отмена' : '+ Новая серия'}
        </MiniBtn>
        {projects.length > 0 && (
          <ContextSelect
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            aria-label="Строительный объект"
          >
            <option value="">— без привязки к объекту —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </ContextSelect>
        )}
      </FormRow>
      {showNewSeries && (
        <FormRow>
          <TitleInput
            value={newSeriesTitle}
            onChange={(e) => setNewSeriesTitle(e.target.value)}
            placeholder="Название серии (напр. Еженедельный штаб по объекту)"
            aria-label="Название новой серии совещаний"
          />
          <MiniBtn
            type="button"
            onClick={() => void handleCreateSeries()}
            disabled={creatingSeries || !newSeriesTitle.trim()}
          >
            Создать серию
          </MiniBtn>
        </FormRow>
      )}
      <FileDropZone
        accept={RECORDING_ACCEPT}
        hint="Перетащите файл записи (mp3/wav/m4a/mp4/webm…) или выберите его."
        onFile={handleFile}
      />
      {isLoading && <Note $tone="muted">Загружаю запись…</Note>}
      {done && (
        <Note $tone="success">
          Запись принята: «{done}». Вы получите уведомление в колокольчик, когда черновик протокола
          будет готов.
        </Note>
      )}
      {error && <Note $tone="danger">{error}</Note>}
    </UploadCard>
  );
}
